// Yakinlik/tarama hatalari (NOT_IN_RANGE, ERROR) izin eksikligi degildir.
const BLUETOOTH_PERMISSION_BLOCKED_ACTIONS = new Set([
  'BLUETOOTH_DENIED',
  'BLUETOOTH_PERMISSION_BLOCKED',
  'BLUETOOTH_PERMISSION_REQUIRED',
  'BLUETOOTH_NOT_SUPPORTED',
])

const NATIVE_PERMISSION_GRANTED = new Set(['granted'])

const GEOLOCATION_PERMISSION_BLOCKED_REASONS = new Set(['PERMISSION_DENIED', 'permission_denied', 'NOT_SUPPORTED', 'not_supported'])

const createRequestId = () => {
  const browserCrypto = typeof window !== 'undefined' ? window.crypto : null

  if (typeof browserCrypto?.randomUUID === 'function') {
    return browserCrypto.randomUUID()
  }

  return `hbgs-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

const createNonce = () => {
  const bytes = new Uint8Array(16)
  const browserCrypto = typeof window !== 'undefined' ? window.crypto : null

  if (typeof browserCrypto?.getRandomValues === 'function') {
    browserCrypto.getRandomValues(bytes)
  } else {
    bytes.forEach((_, index) => {
      bytes[index] = Math.floor(Math.random() * 256)
    })
  }

  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

const isBluetoothPermissionGranted = (bluetoothResult) => {
  if (!bluetoothResult?.action) {
    return false
  }

  if (bluetoothResult.action === 'BLUETOOTH_NOT_SUPPORTED' && bluetoothResult?.payload?.reason === 'FILTER_NOT_CONFIGURED') {
    return true
  }

  return !BLUETOOTH_PERMISSION_BLOCKED_ACTIONS.has(bluetoothResult.action)
}

const isGeolocationPermissionGranted = (geolocationResult) => {
  if (!geolocationResult) {
    return false
  }

  if (geolocationResult.ok) {
    return true
  }

  return !GEOLOCATION_PERMISSION_BLOCKED_REASONS.has(geolocationResult.reason)
}

const resolvePermissionState = (bluetoothResult, geolocationResult) => {
  return {
    bluetoothGranted: isBluetoothPermissionGranted(bluetoothResult),
    geolocationGranted: isGeolocationPermissionGranted(geolocationResult),
  }
}

const isNativePermissionGranted = (status) => {
  return NATIVE_PERMISSION_GRANTED.has(status)
}

const resolvePermissionStateFromNative = (nativePermissions) => {
  return {
    bluetoothGranted: isNativePermissionGranted(nativePermissions?.bluetooth),
    geolocationGranted: isNativePermissionGranted(nativePermissions?.geolocation),
  }
}

const areMandatoryPermissionsGranted = (permissionState) => {
  return Boolean(permissionState?.bluetoothGranted) && Boolean(permissionState?.geolocationGranted)
}

const isBarrierProximityVerified = (bluetoothResult, geolocationResult) => {
  return Boolean(bluetoothResult?.ok) || Boolean(geolocationResult?.ok)
}

const getNullableValue = (value) => {
  return value ?? null
}

const buildBarrierVerificationPayload = (bluetoothResult, geolocationResult, metadata = {}) => {
  const isGeolocationVerified = Boolean(geolocationResult?.ok)
  const geolocationPayload = isGeolocationVerified
    ? {
        ok: true,
        distance: geolocationResult?.distance ?? null,
        lat: getNullableValue(geolocationResult?.lat),
        lon: getNullableValue(geolocationResult?.lon),
        accuracyM: getNullableValue(geolocationResult?.accuracyM),
      }
    : {
        ok: false,
        reason: geolocationResult?.reason || null,
        distance: geolocationResult?.distance ?? null,
        lat: null,
        lon: null,
        accuracyM: null,
      }

  return {
    request_id: metadata.requestId || metadata.request_id || createRequestId(),
    nonce: metadata.nonce || createNonce(),
    timestamp: metadata.timestamp ?? Date.now(),
    bluetoothVerification: {
      ok: Boolean(bluetoothResult?.ok),
      action: bluetoothResult?.action || null,
      payload: bluetoothResult?.payload || {},
    },
    geolocationVerification: geolocationPayload,
  }
}

// Banner gosterilen detayli izin bilgisi: hangi izin eksik, neden, ne yapilmali.
const buildPermissionDetails = (bluetoothResult, geolocationResult) => {
  const details = []

  if (!isBluetoothPermissionGranted(bluetoothResult)) {
    const action = bluetoothResult?.action
    const reason = bluetoothResult?.payload?.reason
    let hint = 'Asagidaki "Izin Ver" butonuna basin, acilan pencereden onaylayin.'
    if (action === 'BLUETOOTH_NOT_SUPPORTED' && reason === 'RN_BRIDGE_NOT_AVAILABLE') {
      hint = 'Uygulamayi kapatip yeniden acin. Eski surum kullaniyorsaniz guncelleyin.'
    } else if (action === 'BLUETOOTH_PERMISSION_BLOCKED' || reason === 'BLOCKED') {
      hint = 'Bluetooth izni daha once kalici reddedildi. "Izin Ver" butonuna bastiginizda yonlendireceginiz ayarlardan Bluetooth iznini elle etkinlestirin.'
    } else if (action === 'BLUETOOTH_NOT_SUPPORTED') {
      hint = 'Cihazinizin Bluetooth desteği bulunmuyor.'
    }
    details.push({
      key: 'bluetooth',
      label: 'Bluetooth',
      status: 'kapali',
      hint,
    })
  }

  if (!isGeolocationPermissionGranted(geolocationResult)) {
    const reason = geolocationResult?.reason
    let hint = 'Asagidaki "Izin Ver" butonuna basin, acilan pencereden onaylayin.'
    if (reason === 'permission_denied') {
      hint = '"Izin Ver" butonuna basin ve "Uygulamayi kullanirken izin ver" secin.'
    } else if (reason === 'blocked' || reason === 'BLOCKED') {
      hint = 'Konum izni kalici reddedildi. "Izin Ver" butonuna bastiginizda yonlendireceginiz ayarlardan elle etkinlestirin.'
    } else if (reason === 'not_supported') {
      hint = 'Cihaziniz konum servisini desteklemiyor.'
    } else if (reason === 'unavailable') {
      hint = 'Konum servisi acik degil. Telefonun bildirim cubugundan GPS / Konum modulunu acin.'
    } else if (reason === 'timeout') {
      hint = 'Konum okuma zaman asimina ugradi. Acik alana cikin veya GPS ayarini "Yuksek Hassasiyet"e alin.'
    }
    details.push({
      key: 'geolocation',
      label: 'Konum',
      status: 'kapali',
      hint,
    })
  }

  return details
}

const buildNativePermissionHint = (kind, status) => {
  if (status === 'blocked') {
    return kind === 'bluetooth'
      ? 'Bluetooth izni kalici reddedildi. "Izin Ver" butonuna bastiginizda acilan ayarlardan Bluetooth iznini elle etkinlestirin.'
      : 'Konum izni kalici reddedildi. "Izin Ver" butonuna bastiginizda acilan ayarlardan konum iznini elle etkinlestirin.'
  }

  if (status === 'unavailable') {
    return kind === 'bluetooth'
      ? 'Cihazinizin Bluetooth destegi bulunmuyor.'
      : 'Konum servisi kullanilamiyor. Telefonunuzda GPS / Konum modulunu acin.'
  }

  if (status === 'denied') {
    return 'Asagidaki "Izin Ver" butonuna basin, acilan pencereden onaylayin.'
  }

  return 'Asagidaki "Izin Ver" butonuna basin, acilan pencereden onaylayin.'
}

const buildPermissionDetailsFromNative = (nativePermissions) => {
  const details = []

  if (!isNativePermissionGranted(nativePermissions?.bluetooth)) {
    details.push({
      key: 'bluetooth',
      label: 'Bluetooth',
      status: 'kapali',
      hint: buildNativePermissionHint('bluetooth', nativePermissions?.bluetooth),
    })
  }

  if (!isNativePermissionGranted(nativePermissions?.geolocation)) {
    details.push({
      key: 'geolocation',
      label: 'Konum',
      status: 'kapali',
      hint: buildNativePermissionHint('geolocation', nativePermissions?.geolocation),
    })
  }

  return details
}

export {
  isBluetoothPermissionGranted,
  isGeolocationPermissionGranted,
  isBarrierProximityVerified,
  resolvePermissionState,
  resolvePermissionStateFromNative,
  areMandatoryPermissionsGranted,
  buildBarrierVerificationPayload,
  buildPermissionDetails,
  buildPermissionDetailsFromNative,
}
